import { activityFetch as fetch } from '../../components/feedback/requestActivity'
import { useCallback, useEffect, useState } from 'react'
import AgentMonitoring from './AgentMonitoring'
import AgentOnboarding from './AgentOnboarding'
import PipelineMonitoring from './PipelineMonitoring'
import NodePerformance from './NodePerformance'
import ImageCatalog from './ImageCatalog'
import WorkloadSettings from './WorkloadSettings'
import DevelopmentRunPanel from './DevelopmentRunPanel'
import ManagedLifecycle from './ManagedLifecycle'
import CatalogClassification from './CatalogClassification'
import './style.css'

const blankService = {
  serviceId: '', displayName: '', repositoryUrl: '',
  devRuntime: 'podman', devName: '', prodRuntime: 'docker', prodName: '', publicUrl: '',
}
const defaultPolicy = {
  autoDeployDev: false, productionApproval: true,
  minReplicas: 1, maxReplicas: 1, scaleOutCpuPercent: 75,
  scaleInCpuPercent: 35, cooldownSeconds: 300,
}
const defaultPreview = { currentReplicas: 1, cpuPercent: 80, secondsSinceLastScale: 300 }
const blankTargets = { developmentPeerId: null, productionPeerId: null }

function headers(json = false) {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token')
  return { ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}) }
}

function message(code) {
  if (code === 'REGISTRY_UNAVAILABLE') return '이미지 저장소를 확인할 수 없습니다.'
  return ({
    LOGIN_REQUIRED: '로그인이 필요합니다.',
    INVALID_SESSION: '로그인이 만료됐습니다. 다시 로그인해 주세요.',
    ADMIN_REQUIRED: '스카이넷 관리자 권한이 필요합니다.',
    AUTH_UNAVAILABLE: '관리자 권한을 확인할 수 없습니다.',
    CATALOG_UNAVAILABLE: '서비스 목록을 불러올 수 없습니다.',
    POLICY_UNAVAILABLE: '확장 정책을 불러오거나 저장할 수 없습니다. 서버 설정을 확인해 주세요.',
    AGENTS_UNAVAILABLE: '에이전트 상태를 읽을 수 없습니다.',
    AGENT_TARGET_UNAVAILABLE: '배포 대상 에이전트를 읽거나 저장할 수 없습니다.',
    AGENT_NOT_OWNED_OR_LINUX: '본인 소유의 리눅스 에이전트만 지정할 수 있습니다.',
    AGENT_NOT_READY: '최근 접속한 본인 소유 리눅스 에이전트만 지정할 수 있습니다.',
    INVALID_AGENT_TARGET: '에이전트 선택값을 확인해 주세요.',
    SERVICE_ALREADY_EXISTS: '같은 서비스가 다른 설정으로 등록돼 있습니다.',
    SERVICE_NOT_FOUND: '등록된 서비스를 찾을 수 없습니다.',
    SOFTWARE_CATALOG_REQUIRED: '설치형 앱은 소프트웨어 출시에서 관리해 주세요.',
    INVALID_SERVICE: '서비스 정보와 배포 대상 이름을 확인해 주세요.',
    INVALID_POLICY: '복제 수와 CPU 임계값을 확인해 주세요.',
    INVALID_PREVIEW: '미리보기 수치를 확인해 주세요.',
    K8S_TARGET_REQUIRED: '개발 대상이 Kubernetes인 서비스를 선택해 주세요.',
    CLUSTER_UNAVAILABLE: '개발 클러스터의 노드 상태를 확인할 수 없습니다.',
    PIPELINE_UNAVAILABLE: '배포 실행 이력을 읽을 수 없습니다. 이력 테이블과 서버 상태를 확인해 주세요.',
    WORKLOAD_UNAVAILABLE: '서비스 실행 설정을 읽거나 저장할 수 없습니다.',
    WORKLOAD_REVISION_CONFLICT: '다른 곳에서 설정이 변경됐습니다. 다시 불러온 뒤 수정해 주세요.',
    INVALID_WORKLOAD_SPEC: '포트·상태 검사·자원·비밀 참조·저장소·공개 경로를 확인해 주세요.',
    EXECUTOR_UNAVAILABLE: '실행기가 연결되지 않았습니다. 연결 상태를 다시 확인해 주세요.',
    LIFECYCLE_UNAVAILABLE: '단계별 실행 상태를 확인할 수 없습니다.',
    EXECUTOR_UPGRADE_REQUIRED:'자동화 실행기 갱신이 필요합니다. 기존 단계별 배포 상태를 확인해 주세요.',
    INVALID_AUTOMATION_POLICY:'자동 실행 범위와 운영 반영 동의를 확인해 주세요.',
    PROCESS_REVISION_CHANGED:'개발 프로세스 버전이 변경됐습니다. 최신 상태를 확인한 뒤 다시 실행해 주세요.',
    LIFECYCLE_RUN_ACTIVE: '이미 진행 중인 작업이 있습니다. 완료 후 다시 실행해 주세요.',
    INVALID_LIFECYCLE_REQUEST: '소스 커밋과 운영 반영 동의를 확인해 주세요.',
    REQUEST_KEY_CONFLICT: '기존 요청과 입력값이 다릅니다. 실행 이력을 확인해 주세요.',
    INITIAL_STAGE_REQUIRED: '먼저 빌드와 PM2 검증을 완료해 주세요.',
    STABILIZATION_REQUIRED: '먼저 K3s 복구·확장 검증을 완료해 주세요.',
    VERIFIED_ARTIFACT_REQUIRED: '검증된 이미지가 필요합니다. 먼저 빌드해 주세요.',
  })[code] || '요청을 처리하지 못했습니다.'
}

async function api(path, options = {}) {
  const response = await fetch(`/api/deploy${path}`, {
    credentials: 'include', headers: headers(Boolean(options.body)), ...options,
  })
  const body = await response.json()
  if (!response.ok) throw Error(message(body.code))
  return body
}

const reasonText = {
  'scale-out': 'CPU 사용량이 상한에 도달해 1개 확장을 권장합니다.',
  'scale-in': 'CPU 사용량이 하한 아래라 1개 축소를 권장합니다.',
  'below-minimum': '최소 복제 수까지 확장을 권장합니다.',
  'above-maximum': '최대 복제 수까지 축소를 권장합니다.',
  cooldown: '대기 시간이 끝나지 않아 변경하지 않습니다.',
  'within-threshold': '현재 수를 유지합니다.',
}

const capabilityStages = [
  { title: '서비스 추가', state: '구성 저장 가능', tone: 'available', detail: 'PM2 프로세스·Docker·Podman·Kubernetes 대상을 등록할 수 있습니다.' },
  { title: '이미지 빌드·배포', state: '화면 실행 미연결', tone: 'pending', detail: '등록만으로 이미지를 만들거나 컨테이너를 시작하지 않습니다.' },
  { title: '노드·부하분산 연결', state: '화면 실행 미연결', tone: 'pending', detail: '노드 상태는 조회할 수 있지만 서비스 경로와 트래픽은 바뀌지 않습니다.' },
  { title: '자동확장·복구', state: '미리보기만 가능', tone: 'pending', detail: '확장 기준을 저장·시험할 수 있지만 실제 확장과 복구는 실행되지 않습니다.' },
]

function initialService() {
  const params = new URLSearchParams(window.location.search)
  const dev = /^k8s:(antsnest)\/([a-z][a-z0-9-]{1,47})$/.exec(params.get('devTarget') || '')
  const prod = /^podman:([a-z0-9][a-z0-9_.-]{0,63})$/.exec(params.get('prodTarget') || '')
  return { ...blankService,
    ...(dev ? { serviceId: dev[2], displayName: dev[2], devRuntime: 'k8s', devName: `${dev[1]}/${dev[2]}` } : {}),
    ...(prod ? { prodRuntime: 'podman', prodName: prod[1] } : {}),
  }
}

export default function Deployments() {
  const [services, setServices] = useState([])
  const [selected, setSelected] = useState(() => new URLSearchParams(window.location.search).get('service') || '')
  const [service, setService] = useState(initialService)
  const [editing, setEditing] = useState(false)
  const [tab,setTab]=useState(()=>window.location.hash==='#agent-onboarding'?'infrastructure':'release')
  const activeTab=tab
  const [agents, setAgents] = useState([])
  const [targets, setTargets] = useState(blankTargets)
  const [agentError, setAgentError] = useState('')
  const [policy, setPolicy] = useState(defaultPolicy)
  const [configured, setConfigured] = useState(false)
  const [previewInput, setPreviewInput] = useState(defaultPreview)
  const [preview, setPreview] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [clusterReadiness, setClusterReadiness] = useState(null)
  const [clusterError, setClusterError] = useState('')

  const loadServices = useCallback(async (signal) => {
    setBusy(true)
    setError('')
    try {
      const body = await api('/services', { signal })
      setServices(body.services || [])
      if (!body.services?.length && window.location.hash!=='#agent-onboarding') setTab('configuration')
      setSelected(previous => (body.services || []).some(item => item.service_id === previous)
        ? previous : (body.services?.find(item=>item.service_id==='skynetmanager')?.service_id || body.services?.[0]?.service_id || ''))
    } catch (cause) {
      if (cause.name !== 'AbortError') setError(cause.message)
    } finally { if (!signal?.aborted) setBusy(false) }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    loadServices(controller.signal)
    return () => controller.abort()
  }, [loadServices])

  const loadAgents = useCallback(async (signal) => {
    try {
      const body = await api('/agents', { signal })
      setAgents(body.agents || []); setAgentError('')
    } catch (cause) { if (cause.name !== 'AbortError') setAgentError(cause.message) }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    loadAgents(controller.signal)
    const interval = setInterval(() => loadAgents(controller.signal), 30000)
    return () => { clearInterval(interval); controller.abort() }
  }, [loadAgents])

  useEffect(() => {
    setClusterReadiness(null); setClusterError('')
    if (!selected) { setPolicy(defaultPolicy); setTargets(blankTargets); setConfigured(false); setPreview(null); return }
    const controller = new AbortController()
    api(`/services/${encodeURIComponent(selected)}/policy`, { signal: controller.signal })
      .then(body => {
        const { configured: saved, ...values } = body.policy
        setPolicy(values)
        setConfigured(saved)
        setPreview(null)
      })
      .catch(cause => { if (cause.name !== 'AbortError') setError(cause.message) })
    api(`/services/${encodeURIComponent(selected)}/agents`, { signal: controller.signal })
      .then(body => setTargets({ developmentPeerId: body.targets.development_peer_id,
        productionPeerId: body.targets.production_peer_id }))
      .catch(cause => { if (cause.name !== 'AbortError') setAgentError(cause.message) })
    return () => controller.abort()
  }, [selected])

  async function register(event) {
    event.preventDefault()
    setBusy(true); setError(''); setNotice('')
    try {
      const body = await api(editing ? `/services/${encodeURIComponent(service.serviceId)}` : '/services',
        { method: editing ? 'PUT' : 'POST', body: JSON.stringify({
        serviceId: service.serviceId, displayName: service.displayName,
        repositoryUrl: service.repositoryUrl,
        devTarget: service.devRuntime === 'unconfigured' ? 'unconfigured:development' : `${service.devRuntime}:${service.devName}`,
        prodTarget: service.prodRuntime === 'unconfigured' ? 'unconfigured:production' : `${service.prodRuntime}:${service.prodName}`,
        publicUrl: service.publicUrl || null,
      }) })
      await loadServices()
      setSelected(body.service.service_id)
      setNotice(editing ? '서비스 구성을 수정했습니다. 실행 중인 컨테이너는 변경하지 않았습니다.'
        : '서비스 구성을 등록했습니다. 이미지 빌드와 배포는 아직 실행되지 않았습니다.')
      setService(blankService); setEditing(false)
    } catch (cause) { setError(cause.message) }
    finally { setBusy(false) }
  }

  function editExisting(item) {
    const [devRuntime, ...devName] = item.dev_target.split(':')
    const [prodRuntime, ...prodName] = item.prod_target.split(':')
    setService({ serviceId: item.service_id, displayName: item.display_name,
      repositoryUrl: item.repository_url, publicUrl: item.public_url || '',
      devRuntime, devName: devName.join(':'), prodRuntime, prodName: prodName.join(':') })
    setEditing(true); setSelected(item.service_id); setError(''); setNotice('')
  }

  async function savePolicy(event) {
    event.preventDefault()
    setBusy(true); setError(''); setNotice('')
    try {
      const body = await api(`/services/${encodeURIComponent(selected)}/policy`,
        { method: 'PUT', body: JSON.stringify(policy) })
      const { configured: saved, ...values } = body.policy
      setPolicy(values); setConfigured(saved); setPreview(null)
      setNotice('배포·확장 정책을 저장했습니다. 실제 배포와 자동 확장은 아직 실행되지 않습니다.')
    } catch (cause) { setError(cause.message) }
    finally { setBusy(false) }
  }

  async function showPreview(event) {
    event.preventDefault()
    setBusy(true); setError(''); setPreview(null)
    try {
      const body = await api(`/services/${encodeURIComponent(selected)}/scale-preview`,
        { method: 'POST', body: JSON.stringify(previewInput) })
      setPreview(body.preview)
    } catch (cause) { setError(cause.message) }
    finally { setBusy(false) }
  }

  async function saveTargets(event) {
    event.preventDefault()
    setBusy(true); setError(''); setNotice('')
    try {
      const body = await api(`/services/${encodeURIComponent(selected)}/agents`,
        { method: 'PUT', body: JSON.stringify(targets) })
      setTargets({ developmentPeerId: body.targets.development_peer_id,
        productionPeerId: body.targets.production_peer_id })
      setNotice('대상 에이전트를 저장했습니다. 배포 명령은 아직 전송되지 않았습니다.')
    } catch (cause) { setError(cause.message) }
    finally { setBusy(false) }
  }

  async function checkCluster() {
    if (!selected) return
    setBusy(true); setClusterReadiness(null); setClusterError('')
    try {
      const body = await api(`/services/${encodeURIComponent(selected)}/k8s-readiness`)
      if (!body.readOnly || body.deploymentEnabled !== false) throw Error('클러스터 조회 결과를 검증할 수 없습니다.')
      setClusterReadiness(body)
    } catch (cause) { setClusterError(cause.message) }
    finally { setBusy(false) }
  }

  const serviceField = (key, label, placeholder, required = true) => <label key={key}>{label}
    <input value={service[key]} placeholder={placeholder} maxLength={300} required={required}
      onChange={event => setService(current => ({ ...current, [key]: event.target.value }))} />
  </label>
  const numberField = (key, label, min, max) => <label key={key}>{label}
    <input type="number" min={min} max={max} value={policy[key]}
      onChange={event => setPolicy(current => ({ ...current, [key]: Number(event.target.value) }))} />
  </label>
  const previewField = (key, label, min, max) => <label key={key}>{label}
    <input type="number" min={min} max={max} value={previewInput[key]}
      onChange={event => setPreviewInput(current => ({ ...current, [key]: Number(event.target.value) }))} />
  </label>
  const setupStages = [
    { href: '#agent-onboarding', label: '머신 등록', state: '수동 확인', complete: false },
    { href: '#register-heading', label: '서비스 등록', state: selected ? '등록됨' : '미등록', complete: Boolean(selected) },
    { href: '#agents-heading', label: '개발·운영 노드 지정',
      state: targets.developmentPeerId && targets.productionPeerId ? '지정됨' : '미지정',
      complete: Boolean(targets.developmentPeerId && targets.productionPeerId), disabled: !selected },
    { href: '#policy-heading', label: '배포·확장 정책', state: configured ? '저장됨' : '미저장',
      complete: configured, disabled: !selected },
    { href: '#scale-preview-heading', label: '확장 판단 미리보기', state: preview ? '확인됨' : '미실행',
      complete: Boolean(preview), disabled: !selected },
    { label: 'K8s 노드·부하분산 연결', state: '미구현', complete: false, disabled: true },
  ]
  const selectedService = services.find(item => item.service_id === selected)
  const k8sDevelopment = selectedService?.dev_target?.startsWith('k8s:') === true
  function selectTab(value) {
    setTab(value)
    if (value==='configuration' && selectedService && !editing && !service.serviceId) editExisting(selectedService)
  }

  return <main className="deploy-page">
    <header><span className="console-eyebrow">SERVER DELIVERY</span><h1>서버 배포</h1>
      <p>서버 서비스를 선택해 배포 이력과 개발·운영 설정을 확인하세요. <a href="/software">앱·설치 파일은 소프트웨어 출시에서 관리 →</a></p>
      <a className="deploy-new" href={`/deploy/plan${selected ? `?service=${encodeURIComponent(selected)}` : ''}`}>무중단 배포 설계 →</a></header>
    {error && <p className="deploy-error" role="alert">{error}</p>}
    {notice && <p className="deploy-notice" role="status">{notice}</p>}


    <section className="deploy-panel" aria-labelledby="registered-heading">
      <div className="deploy-section-heading"><h2 id="registered-heading">등록된 서비스</h2>
        <button type="button" onClick={() => loadServices()} disabled={busy}>새로고침</button></div>
      {services.length ? <div className="deploy-service-picker">
        <label htmlFor="deploy-selected-service">서비스 선택</label>
        <select id="deploy-selected-service" value={selected} onChange={event => {
          setSelected(event.target.value); setEditing(false); setService(blankService); setError(''); setNotice('')
          if(activeTab==='configuration') editExisting(services.find(item=>item.service_id===event.target.value))
        }}>{services.map(item => <option key={item.service_id} value={item.service_id}>{item.display_name}</option>)}</select>
        <span>{services.length}개 등록</span>
        <button type="button" onClick={() => { editExisting(selectedService); setTab('configuration') }} disabled={!selectedService}>선택 서비스 구성</button>
        <button type="button" onClick={()=>{setService(blankService);setEditing(false);setTab('configuration');setError('');setNotice('')}}>서비스 추가</button>
      </div> : !error && !busy && <p>등록된 서비스가 없습니다.</p>}
    </section>

    <div className="console-tabs" role="tablist" aria-label="배포 작업" onKeyDown={event=>{
      if(!['ArrowLeft','ArrowRight','Home','End'].includes(event.key))return
      event.preventDefault()
      const keys=['release','history','configuration','infrastructure'];const current=keys.indexOf(activeTab)
      const next=event.key==='Home'?0:event.key==='End'?3:(current+(event.key==='ArrowRight'?1:3))%4
      selectTab(keys[next]);document.getElementById(`deploy-tab-${keys[next]}`)?.focus()
    }}>{[['release','단계별 배포'],['history','이력 · 이미지'],['configuration','서비스 구성'],['infrastructure','노드 · 확장']].map(([key,label])=><button key={key} id={`deploy-tab-${key}`} role="tab" aria-selected={activeTab===key} tabIndex={activeTab===key?0:-1} aria-controls={`deploy-panel-${key}`} onClick={()=>selectTab(key)}>{label}</button>)}</div>
    <section id="deploy-panel-release" role="tabpanel" aria-labelledby="deploy-tab-release" hidden={activeTab!=='release'}>
      <ManagedLifecycle service={selectedService} api={api} />
      {!['estimate-app','skynetmanager'].includes(selected) && <><div className="deploy-stage-intro"><h2>선택한 서비스의 배포 준비</h2><p>초기 개발은 Ansible + PM2, 안정화는 K3s, 운영은 Kubernetes로 전환합니다. 이 서비스의 자동 실행 연결 상태를 아래에서 확인하세요.</p></div><DevelopmentRunPanel service={selectedService} api={api}/></>}
    </section>
    <section id="deploy-panel-history" role="tabpanel" aria-labelledby="deploy-tab-history" hidden={activeTab!=='history'}><PipelineMonitoring service={selectedService} api={api}/><ImageCatalog service={selectedService} api={api}/></section>
    <section id="deploy-panel-configuration" role="tabpanel" aria-labelledby="deploy-tab-configuration" hidden={activeTab!=='configuration'}>

    <section className="deploy-panel" aria-labelledby="register-heading"><h2 id="register-heading">{editing ? '서비스 구성 수정' : '서비스 구성 등록'}</h2>
      <p>프로세스 또는 컨테이너 대상을 등록합니다. 인증정보·비밀번호·토큰은 입력하지 마세요.</p>
      {editing && <button className="deploy-new" type="button" onClick={() => {
        setService(blankService); setEditing(false); setError(''); setNotice('')
      }}>새 서비스 등록</button>}
      <form className="deploy-form" onSubmit={register}>
        {editing ? <label>서비스 식별자<input value={service.serviceId} disabled readOnly /></label>
          : serviceField('serviceId', '서비스 식별자', 'my-service')}
        {serviceField('displayName', '표시 이름', '내 서비스')}
        {serviceField('repositoryUrl', '소스 저장소', 'https://github.com/AntsNest/MyService (선택)', false)}
        {serviceField('publicUrl', '공개 주소 (선택)', 'https://example.com', false)}
        <fieldset className="deploy-target"><legend>개발 환경</legend>
          <label>배포 방식<select value={service.devRuntime}
            onChange={event => setService(current => ({ ...current, devRuntime: event.target.value }))}>
            <option value="unconfigured">미연결</option><option value="systemd">시스템 서비스</option><option value="static">정적 사이트</option>
            <option value="pm2">프로세스 (PM2)</option><option value="podman">Podman</option><option value="docker">Docker</option>
            <option value="k8s">Kubernetes (개발 클러스터)</option></select></label>
          {service.devRuntime !== 'unconfigured' && serviceField('devName', service.devRuntime === 'k8s' ? '네임스페이스/디플로이먼트' : service.devRuntime === 'pm2' ? '프로세스 이름' : ['systemd', 'static'].includes(service.devRuntime) ? '서버/실행 대상' : '컨테이너 이름',
            service.devRuntime === 'k8s' ? 'antsnest/my-service-dev' : 'my-service-dev')}
        </fieldset>
        <fieldset className="deploy-target"><legend>운영 환경</legend>
          <label>배포 방식<select value={service.prodRuntime}
            onChange={event => setService(current => ({ ...current, prodRuntime: event.target.value }))}>
            <option value="unconfigured">미연결</option><option value="systemd">시스템 서비스</option><option value="static">정적 사이트</option>
            <option value="pm2">프로세스 (PM2)</option><option value="podman">Podman</option><option value="docker">Docker</option>
            <option value="k8s">Kubernetes (운영 클러스터)</option></select></label>
          {service.prodRuntime !== 'unconfigured' && serviceField('prodName', service.prodRuntime === 'k8s' ? '네임스페이스/디플로이먼트' : service.prodRuntime === 'pm2' ? '프로세스 이름' : ['systemd', 'static'].includes(service.prodRuntime) ? '서버/실행 대상' : '컨테이너 이름',
            service.prodRuntime === 'k8s' ? 'antsnest/my-service-prod' : 'my-service-prod')}
        </fieldset>
        <p>아직 준비되지 않은 환경은 미연결로 등록할 수 있습니다. 다른 서버의 대상은 서버/이름으로 구분하며, 관측 연결 전에는 상태를 확인 필요로 표시합니다.</p>
        <button type="submit" disabled={busy}>{busy ? '확인 중…' : editing ? '구성 수정' : '서비스 등록'}</button>
      </form>
    </section>

    <details className="deploy-disclosure"><summary>워크로드 실행 설정</summary><WorkloadSettings service={selectedService} api={api}/></details>
    <CatalogClassification key={selected} service={selectedService} onChanged={async()=>{await loadServices();setService(blankService);setEditing(false);setNotice('소프트웨어 출시로 옮겼습니다. 서버 관제 대상에서 제외되었습니다.')}}/>
    </section>
    <section id="deploy-panel-infrastructure" role="tabpanel" aria-labelledby="deploy-tab-infrastructure" hidden={activeTab!=='infrastructure'}>
    {selected && <section className="deploy-panel" aria-labelledby="agents-heading">
      <div className="deploy-section-heading"><h2 id="agents-heading">배포 대상 에이전트</h2>
        <button type="button" onClick={() => loadAgents()} disabled={busy}>상태 새로고침</button></div>
      <p>최근 2분 이내 보고한 에이전트만 온라인입니다. 실행 방식 설치 여부는 별도 검증이 필요합니다.</p>
      {agentError && <p className="deploy-error" role="alert">{agentError}</p>}
      {!agentError && <AgentMonitoring agents={agents} />}
      <form className="deploy-form deploy-targets" onSubmit={saveTargets}>
        {[['developmentPeerId', '개발 대상'], ['productionPeerId', '운영 대상']].map(([key, label]) => <label key={key}>{label}
          <select value={targets[key] || ''} onChange={event => setTargets(current => ({ ...current,
            [key]: event.target.value ? Number(event.target.value) : null }))}>
            <option value="">미지정</option>
            {agents.filter(agent => agent.online && /^linux/i.test(agent.os_type || '')).map(agent =>
              <option value={agent.peer_id} key={agent.peer_id}>{agent.hostname || `에이전트 ${agent.peer_id}`} · {agent.online ? '온라인' : '오프라인'}</option>)}
          </select></label>)}
        <button type="submit" disabled={busy || Boolean(agentError)}>대상 저장</button>
      </form>
      <p className="deploy-warning">대상 지정은 배포가 아닙니다. 에이전트 인증·실행기 검증 전까지 실행 버튼을 제공하지 않습니다.</p>
    </section>}

    {selected && <section className="deploy-panel" aria-labelledby="policy-heading">
      <h2 id="policy-heading">배포·자동확장 정책 <small>— {selected}</small></h2>
      <p>{configured ? '저장된 정책' : '기본값'} · 실제 노드 연결 전에는 실행되지 않습니다.</p>
      <form className="deploy-form" onSubmit={savePolicy}>
        <label className="deploy-check"><input type="checkbox" checked={policy.autoDeployDev}
          onChange={event => setPolicy(current => ({ ...current, autoDeployDev: event.target.checked }))} />
          개발 환경 자동배포 요청 허용 (실행기 연결 전에는 저장만)</label>
        <label className="deploy-check"><input type="checkbox" checked={policy.productionApproval} disabled readOnly />
          운영 전환은 관리자 승인 필요</label>
        {numberField('minReplicas', '최소 실행 수', 1, 20)}
        {numberField('maxReplicas', '최대 실행 수', 1, 20)}
        {numberField('scaleOutCpuPercent', '확장 CPU 기준 (%)', 20, 95)}
        {numberField('scaleInCpuPercent', '축소 CPU 기준 (%)', 10, 85)}
        {numberField('cooldownSeconds', '변경 후 대기 시간 (초)', 60, 3600)}
        <button type="submit" disabled={busy}>정책 저장</button>
      </form>
      <form className="deploy-preview" onSubmit={showPreview}>
        <h3 id="scale-preview-heading">확장 판단 미리보기</h3><p>입력한 가상 수치로만 계산하며 노드는 변경하지 않습니다.</p>
        <div className="deploy-form">
          {previewField('currentReplicas', '현재 실행 수', 0, 20)}
          {previewField('cpuPercent', '평균 CPU 사용량 (%)', 0, 100)}
          {previewField('secondsSinceLastScale', '마지막 변경 후 경과 (초)', 0, 86400)}
          <button type="submit" disabled={busy}>판단 미리보기</button>
        </div>
        {preview && <p className="deploy-notice" role="status">현재 {preview.currentReplicas}개 → 권장 {preview.desiredReplicas}개. {reasonText[preview.reason]} (시뮬레이션·실행 안 함)</p>}
      </form>
    </section>}
    <section className="deploy-panel" aria-labelledby="cluster-heading">
      <h2 id="cluster-heading">배포 전 K8s·트래픽 연결</h2>
      <p>실제 서비스 배포에는 대상 노드의 실행기 준비, K8s 워크로드·서비스 연결, 부하분산 경로와 준비 상태 검증이 필요합니다.</p>
      {selected && k8sDevelopment && <button className="deploy-new" type="button" onClick={checkCluster} disabled={busy}>
        개발 클러스터 노드 상태 확인
      </button>}
      {selected && !k8sDevelopment && <p>선택한 서비스의 개발 대상은 Kubernetes가 아닙니다. K8s 상태 확인은 개발 대상을 Kubernetes로 등록한 경우에만 가능합니다.</p>}
      {clusterError && <p className="deploy-error" role="alert">{clusterError}</p>}
      {clusterReadiness && <div className="deploy-cluster-result" role="status">
        <strong>읽기 전용 노드 확인</strong>
        <p>{clusterReadiness.target?.namespace}/{clusterReadiness.target?.name} · 준비 노드 {clusterReadiness.cluster?.readyNodes ?? 0}/{clusterReadiness.cluster?.totalNodes ?? 0}</p>
        <p>노드 상태만 조회했습니다. 워크로드 실행·서비스 연결·부하분산 전환은 수행하지 않았습니다.</p>
      </div>}
      <p className="deploy-warning">현재 등록 화면은 이 연결을 수행하지 않습니다. 노드를 선택해도 컨테이너 배포나 트래픽 전환은 시작되지 않습니다.</p>
    </section>
    <details className="deploy-disclosure" open={window.location.hash==='#agent-onboarding'||undefined}><summary>연결된 머신과 에이전트 설치</summary><AgentOnboarding /><NodePerformance agents={agents} /></details>
    {!['estimate-app', 'skynetmanager'].includes(selected) && <details className="deploy-disclosure"><summary>구성 단계와 실행 지원 범위</summary>
    <section className="deploy-capabilities" aria-labelledby="deploy-capabilities-heading">
      <div className="deploy-capabilities-heading">
        <h2 id="deploy-capabilities-heading">현재 가능한 범위</h2>
        <p>저장·조회와 실제 배포 실행을 구분해 표시합니다.</p>
      </div>
      <ul>{capabilityStages.map(stage => <li key={stage.title} className={`deploy-capability-${stage.tone}`}>
        <strong>{stage.title}</strong>
        <span>{stage.state}</span>
        <p>{stage.detail}</p>
      </li>)}</ul>
    </section>
    <ol className="deploy-steps" aria-label="배포 단계">
      <li>서비스 등록</li><li>이미지 빌드</li><li>개발 검증</li><li>운영 승인</li><li>운영 전환·복구</li>
    </ol>
    <nav className="deploy-setup" aria-label="서비스 배포 등록 흐름">
      <h2>구성 진행</h2>
      <p>머신 확인 → 서비스 등록 → 개발·운영 노드 지정 → 정책 저장 → 확장 판단 미리보기 → K8s 노드·부하분산 연결 순서입니다. 완료 표시는 배포 실행을 뜻하지 않습니다.</p>
      <ol>{setupStages.map(stage => <li key={stage.label}>
        {stage.disabled ? <span>{stage.label}</span> : <a href={stage.href}>{stage.label}</a>}
        <span className={`deploy-setup-state${stage.complete ? ' complete' : ''}`}>{stage.state}</span>
      </li>)}</ol>
    </nav>
    </details>}
    </section>
  </main>
}
